import { getCriteria } from './session-handler.js';
import {
  _isNumber,
  convertToCurrency,
  enableNewRenovatedPriceTag,
  EventHandler,
  getDateFromDateString,
  getSelectedCurencyCode,
  isAllInPriceRegion,
  replaceToken
} from './utils.js';
import {
  $_PAGE_,
  countryCodeList,
  hotel_check_in,
  hotel_check_out,
  newRenovate_enddate,
  newRenovate_startDate,
  newRenovate_year,
  overview_propertyId,
  property_country_code,
  property_new,
  property_renovated
} from './vars.js';

const SCHEMA_SCRIPT_ID = 'property-hotel-schema';
// Matches appendAmenitiesList() in property-page.js — amenities visible before "see all"
const KEY_AMENITIES_PAGE_LOAD_LIMIT = 6;

function getSchemaScript() {
  return document.getElementById(SCHEMA_SCRIPT_ID);
}

function readSchema() {
  const script = getSchemaScript();
  if (!script || !script.textContent) {
    return null;
  }
  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    console.error('Unable to parse property JSON-LD schema', error);
    return null;
  }
}

function writeSchema(schema) {
  const script = getSchemaScript();
  if (script && schema) {
    script.textContent = JSON.stringify(schema);
  }
}

function mergeSchema(updates) {
  const schema = readSchema();
  if (!schema) {
    return;
  }
  writeSchema(Object.assign(schema, updates));
}

function parseTimeTo24Hour(timeLabel) {
  if (!timeLabel || typeof timeLabel !== 'string') {
    return '15:00:00';
  }
  const match = timeLabel.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!match) {
    return '15:00:00';
  }
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const meridiem = (match[3] || '').toUpperCase();
  if (meridiem === 'PM' && hours < 12) {
    hours += 12;
  }
  if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }
  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
}

function toSchemaDateTime(dateValue, timeLabel) {
  const date = getDateFromDateString(dateValue);
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}T${parseTimeTo24Hour(timeLabel)}`;
}

function resolveAvailabilityListing(data) {
  if (!data) {
    return null;
  }
  if (data.hostBrand && data.hostBrand.availability && data.hostBrand.availability[0]) {
    return data.hostBrand.availability[0];
  }
  if (data.altSell && data.altSell.availability && data.altSell.availability[0]) {
    return data.altSell.availability[0];
  }
  if (data.availability && data.availability.hostBrand && data.availability.hostBrand.availability) {
    return data.availability.hostBrand.availability[0] || null;
  }
  if (data.rate) {
    return data;
  }
  return null;
}

function usesAllInPriceDisplay() {
  return $_PAGE_.is('.property-page')
    && countryCodeList
    && typeof $ !== 'undefined'
    && $('.pricing .taxes-fees').text().trim().length > 0
    && isAllInPriceRegion();
}

function resolveDisplayRate(rate) {
  if (!rate) {
    return null;
  }

  let rawRate = null;
  if (usesAllInPriceDisplay()) {
    if (_isNumber(rate.totalAfterTax) && rate.totalAfterTax > 0) {
      rawRate = rate.totalAfterTax * 1;
    } else if (_isNumber(rate.displayRate) && rate.displayRate > 0) {
      rawRate = rate.displayRate * 1;
    }
  } else if (rate.priceDisplayType && _isNumber(rate.displayRateProcessed) && rate.displayRateProcessed > 0) {
    rawRate = rate.displayRateProcessed * 1;
  } else if (_isNumber(rate.displayRate) && rate.displayRate > 0) {
    rawRate = rate.displayRate * 1;
  }

  if (rawRate === null) {
    return null;
  }

  const sourceCurrency = rate.currencyCode || 'USD';
  return {
    price: parseFloat(convertToCurrency(rawRate, sourceCurrency)),
    currency: getSelectedCurencyCode(sourceCurrency)
  };
}

export function getDisplayedReviewAuthorName(review) {
  if (review.reviewsGuestRewardsLevelDesc) {
    return review.reviewsGuestRewardsLevelDesc;
  }
  return review.reviewsGuestName || 'Guest';
}

function mapReviewToSchema(review) {
  const mappedReview = {
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: getDisplayedReviewAuthorName(review)
    },
    reviewBody: review.reviewsMainComment || review.reviewsMainCommentEnglishTranslation || '',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: Number(review.reviewsRating),
      bestRating: 5,
      worstRating: 1
    }
  };

  if (review.reviewsGuestResponseDate) {
    mappedReview.datePublished = review.reviewsGuestResponseDate;
  }

  return mappedReview;
}

export function updatePropertySchemaOffer(availabilityData) {
  const listing = resolveAvailabilityListing(availabilityData);
  const rate = listing && listing.rate ? listing.rate : null;
  const displayRate = resolveDisplayRate(rate);
  if (!displayRate) {
    return;
  }

  const criteria = getCriteria();
  const schema = readSchema();
  if (!schema) {
    return;
  }

  const checkinTime = toSchemaDateTime(criteria.checkInDate, hotel_check_in);
  const checkoutTime = toSchemaDateTime(criteria.checkOutDate, hotel_check_out);
  const { price, currency } = displayRate;

  schema.makesOffer = {
    '@type': ['Offer', 'LodgingReservation'],
    checkinTime,
    checkoutTime,
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    url: schema.url,
    priceSpecification: {
      '@type': 'CompoundPriceSpecification',
      price,
      priceCurrency: currency
    }
  };

  writeSchema(schema);
}

function mapAmenityFeatures(amenityNames) {
  return amenityNames.map((name) => ({
    '@type': 'LocationFeatureSpecification',
    name,
    value: true
  }));
}

const PROPERTY_BADGE_SELECTORS = [
  '.property-page .new-hotel-label.newly-opened:not(.hidden)',
  '.property-page .new-hotel-label.renovated:not(.hidden)'
];

function buildPropertyBadgeLabel(labelElement, year) {
  if (!labelElement) {
    return '';
  }
  const textElement = labelElement.querySelector('.new-hotel-text');
  let label = textElement ? textElement.textContent.trim() : '';
  if (year) {
    label = `${label} ${decodeURIComponent(year)}`.trim();
  }
  return label;
}

function isPropertyBadgeInDisplayWindow() {
  const startDate = newRenovate_startDate ? decodeURIComponent(newRenovate_startDate) : '';
  const endDate = newRenovate_enddate ? decodeURIComponent(newRenovate_enddate) : '';
  return Boolean(startDate && endDate && enableNewRenovatedPriceTag(startDate, endDate));
}

function getPropertyBadgeAmenityNamesFromConfig() {
  if (!isPropertyBadgeInDisplayWindow()) {
    return [];
  }

  const badgeNames = [];
  if (property_new === 'true') {
    const label = buildPropertyBadgeLabel(
      document.querySelector('.new-hotel-label.newly-opened'),
      newRenovate_year
    );
    if (label) {
      badgeNames.push(label);
    }
  }
  if (property_renovated === 'true') {
    const label = buildPropertyBadgeLabel(
      document.querySelector('.new-hotel-label.renovated'),
      newRenovate_year
    );
    if (label) {
      badgeNames.push(label);
    }
  }
  return badgeNames;
}

function getVisiblePropertyBadgeAmenityNames() {
  const badgeNames = PROPERTY_BADGE_SELECTORS
    .map((selector) => document.querySelector(selector))
    .filter(Boolean)
    .map((element) => element.textContent.trim())
    .filter(Boolean);

  return badgeNames.length ? badgeNames : getPropertyBadgeAmenityNamesFromConfig();
}

function prioritizeBadgeAmenities(amenityNames) {
  const badgeNames = getVisiblePropertyBadgeAmenityNames();
  const regularAmenities = amenityNames.filter((name) => !badgeNames.includes(name));
  return [...badgeNames, ...regularAmenities.slice(0, KEY_AMENITIES_PAGE_LOAD_LIMIT)];
}

function getPageLoadAmenityNamesFromOverview(overviewData) {
  const amenityNames = [];
  const amenityDetails = overviewData.amenitiesDetails || [];

  amenityDetails.forEach((group) => {
    if (amenityNames.length >= KEY_AMENITIES_PAGE_LOAD_LIMIT) {
      return;
    }
    const detail = group.groupDetails && group.groupDetails[0];
    if (detail && detail.amenityName) {
      amenityNames.push(detail.amenityName);
    }
  });

  return prioritizeBadgeAmenities(amenityNames);
}

function buildStreetAddressFromOverview(overviewData) {
  const line1 = overviewData.address1 || '';
  const line2 = overviewData.address2 ? ` ${overviewData.address2}` : '';
  const line3 = overviewData.address3 ? ` ${overviewData.address3}` : '';
  return (line1 + line2 + line3).trim();
}

function formatOverviewPhone(overviewData) {
  let phoneNumber = overviewData.phone1;
  if (!phoneNumber) {
    return '';
  }
  if (property_country_code && ['CN', 'TW', 'HK', 'MO'].includes(property_country_code)) {
    if (phoneNumber.indexOf('+') !== 0) {
      phoneNumber = `+${phoneNumber}`;
    }
  }
  return phoneNumber;
}

function buildAddressFromOverview(overviewData) {
  const street = buildStreetAddressFromOverview(overviewData);
  const address = { '@type': 'PostalAddress' };

  if (street) {
    address.streetAddress = street;
  }
  if (overviewData.city) {
    address.addressLocality = overviewData.city;
  }
  if (overviewData.stateCode && overviewData.stateCode !== '**') {
    address.addressRegion = overviewData.stateCode;
  }
  if (overviewData.postalCode
    && overviewData.postalCode !== '000000'
    && overviewData.postalCode !== '00000') {
    address.postalCode = overviewData.postalCode;
  }

  const country = overviewData.countryCode || property_country_code || overviewData.country;
  if (country) {
    address.addressCountry = country;
  }

  return Object.keys(address).length > 1 ? address : null;
}

function getDescriptionTemplate(schema) {
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && metaDescription.getAttribute('data-override')) {
    return metaDescription.getAttribute('data-override');
  }
  return schema.description || '';
}

function resolveDescriptionTokens(descriptionTemplate, overviewData) {
  if (!descriptionTemplate) {
    return '';
  }

  let resolved = descriptionTemplate;
  const hotelName = overviewData.hotelName || '';
  const city = overviewData.city || '';
  const stateAbr = overviewData.stateCode && overviewData.stateCode !== '**'
    ? overviewData.stateCode
    : (overviewData.countryCode || overviewData.country || '');
  const postalCode = overviewData.postalCode || '';

  if (hotelName) {
    resolved = replaceToken(resolved, '${hotelName}', hotelName) || resolved;
  }
  if (city) {
    resolved = replaceToken(resolved, '${city}', city) || resolved;
  }
  if (stateAbr) {
    resolved = replaceToken(resolved, '${stateAbr}', stateAbr) || resolved;
  }
  if (postalCode && !postalCode.includes('00000')) {
    resolved = replaceToken(resolved, '${zipcode}', postalCode) || resolved;
  } else if (resolved.includes(' ${zipcode}')) {
    resolved = replaceToken(resolved, ' ${zipcode}', '') || resolved;
  } else {
    resolved = replaceToken(resolved, '${zipcode}', '') || resolved;
  }

  return resolved;
}

export function updatePropertySchemaFromOverview(overviewData) {
  if (!overviewData) {
    return;
  }

  const schema = readSchema();
  if (!schema) {
    return;
  }

  const updates = {};

  if (overviewData.hotelName) {
    updates.name = overviewData.hotelName;
  }
  // Keep Hotel Center feed property ID from SSR; do not overwrite with Overview API id.
  if (!schema.identifier && overview_propertyId) {
    updates.identifier = overview_propertyId;
  }

  const phone = formatOverviewPhone(overviewData);
  if (phone) {
    updates.telephone = phone;
  }

  const address = buildAddressFromOverview(overviewData);
  if (address) {
    updates.address = address;
  }

  const resolvedDescription = resolveDescriptionTokens(getDescriptionTemplate(schema), overviewData);
  if (resolvedDescription) {
    updates.description = resolvedDescription;
  }

  mergeSchema(updates);
}

export function updatePropertySchemaAmenitiesFromDom() {
  const amenityNames = [];
  document.querySelectorAll('.hotel-policies-amenities__list .ams-titles').forEach((element) => {
    const name = element.textContent.trim();
    if (name) {
      amenityNames.push(name);
    }
  });

  const prioritizedNames = prioritizeBadgeAmenities(amenityNames);
  if (!prioritizedNames.length) {
    return;
  }

  mergeSchema({
    amenityFeature: mapAmenityFeatures(prioritizedNames)
  });
}

export function updatePropertySchemaAmenities(overviewData) {
  if (!overviewData) {
    return;
  }

  const amenityNames = getPageLoadAmenityNamesFromOverview(overviewData);
  if (!amenityNames.length) {
    return;
  }

  mergeSchema({
    amenityFeature: mapAmenityFeatures(amenityNames)
  });
}

export function updatePropertySchemaRatings(overviewData, reviewsPayload) {
  const schema = readSchema();
  if (!schema) {
    return;
  }

  const propertiesNode = reviewsPayload
    && reviewsPayload.data
    && reviewsPayload.data.searchProperties
    && reviewsPayload.data.searchProperties.properties;

  const reviews = propertiesNode && propertiesNode.reviews;
  const pagination = propertiesNode && propertiesNode.pagination;

  const hasActiveRating = overviewData
    && overviewData.rating
    && overviewData.ratingSiteStatus
    && overviewData.ratingSiteStatus.toLowerCase() === 'active';

  if (hasActiveRating) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(overviewData.rating),
      reviewCount: Number(
        pagination && pagination.totalRecords
          ? pagination.totalRecords
          : overviewData.ratingCount
      ),
      bestRating: 5,
      worstRating: 1
    };
  }

  if (reviews && reviews.length) {
    schema.review = reviews.map(mapReviewToSchema);
  }

  writeSchema(schema);
}

export function initPropertySchemaAugmentation() {
  if (!$_PAGE_.is('.property-page') || !getSchemaScript()) {
    return;
  }

  EventHandler.on(EventHandler.rates.propertyAvailability.success, (event, data) => {
    updatePropertySchemaOffer(data);
  });

  EventHandler.on(EventHandler.property.search.success, (event, overviewData) => {
    updatePropertySchemaAmenities(overviewData);
  });
}
